import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { InputGroupModule } from 'primeng/inputgroup';
import { IftaLabelModule } from 'primeng/iftalabel';
import { SelectModule } from 'primeng/select';
import { Subscription } from 'rxjs';
import { FlexFieldConfig, FlexFieldValue } from 'src/app/shared/flex-fields/flex-field.model';
import { FlexFieldService } from '../flex-field.service';

@Component({
    selector: 'app-flex-field-form',
    standalone: true,
    templateUrl: './flex-field-form.component.html',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        InputTextModule,
        InputGroupModule,
        IftaLabelModule,
        SelectModule
    ]
})
export class FlexFieldFormComponent implements OnChanges, OnDestroy {

    @Input({ required: true }) flexFields!: FormArray;
    @Input({ required: true }) objectName!: string;
    @Input() owningCompanyId?: number | null;
    @Input() existingValues: FlexFieldValue[] | null | undefined;
    /** Field names rendered elsewhere as first-class fields. */
    @Input() excludedFieldNames: string[] = [];
    @Input() containerClass = 'formgrid grid';
    @Input() columnClass = 'field col-12 md:col-4';

    configs: FlexFieldConfig[] = [];
    private values: FlexFieldValue[] = [];
    private configSubscription?: Subscription;

    readonly booleanOptions = [
        { label: 'Yes', value: true },
        { label: 'No', value: false }
    ];

    constructor(
        private readonly fb: FormBuilder,
        private readonly flexFieldService: FlexFieldService
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['existingValues']) {
            this.values = (this.existingValues ?? []).slice();
        }

        if (changes['objectName'] || changes['owningCompanyId']) {
            this.fetchConfigurations();
            return;
        }

        if (changes['existingValues']) {
            this.populateFormArray();
        }
    }

    ngOnDestroy(): void {
        this.cancelSubscription();
    }

    get controls(): FormGroup[] {
        return (this.flexFields?.controls as FormGroup[]) ?? [];
    }

    getLabel(index: number): string {
        const config = this.getConfig(index);
        const label = config?.alternateFieldName?.trim();
        const fallback = config?.fieldName ?? '';
        return label?.length ? label : fallback;
    }

    shouldShowError(index: number): boolean {
        const control = this.getValueControl(index);
        return !!control && control.invalid && (control.dirty || control.touched);
    }

    getErrorMessage(index: number): string {
        const control = this.getValueControl(index);
        if (!control) {
            return '';
        }
        if (control.hasError('required')) {
            return 'This field is required.';
        }
        const maxLengthError = control.getError('maxlength');
        if (maxLengthError) {
            return `Maximum length is ${maxLengthError.requiredLength} characters.`;
        }
        return 'Invalid value.';
    }

    isBoolean(index: number): boolean {
        return this.getDataType(index) === 'BOOLEAN';
    }

    isDate(index: number): boolean {
        return this.getDataType(index) === 'DATE';
    }

    isDateTime(index: number): boolean {
        return this.getDataType(index) === 'DATETIME';
    }

    isNumeric(index: number): boolean {
        const type = this.getDataType(index);
        return type === 'INTEGER' || type === 'DECIMAL';
    }

    getNumericStep(index: number): string {
        return this.getDataType(index) === 'DECIMAL' ? '0.01' : '1';
    }

    private fetchConfigurations(): void {
        if (!this.flexFields || !this.objectName) {
            return;
        }
        this.cancelSubscription();
        this.configSubscription = this.flexFieldService
            .getConfigs(this.objectName, this.owningCompanyId ?? undefined)
            .subscribe({
                next: configs => {
                    this.configs = (configs ?? []).filter(config => !this.isExcluded(config));
                    this.populateFormArray();
                },
                error: () => {
                    this.configs = [];
                    this.populateFormArray();
                }
            });
    }

    private populateFormArray(): void {
        if (!this.flexFields) {
            return;
        }
        const currentValues = this.flexFields.controls.map(control => control.value as FlexFieldValue);
        const sourceValues = this.values.length ? this.values : currentValues;
        this.clearFormArray();
        this.configs.forEach(config => {
            const matchedValue = this.findFlexFieldValue(config, sourceValues);
            this.flexFields.push(this.createFlexFieldFormGroup(config, matchedValue));
        });
    }

    private createFlexFieldFormGroup(
        config: FlexFieldConfig,
        existingValue?: FlexFieldValue
    ): FormGroup {
        const validators = [];
        if (config.required) {
            validators.push(Validators.required);
        }
        if (config.fieldLength) {
            validators.push(Validators.maxLength(config.fieldLength));
        }
        return this.fb.group({
            configId: [existingValue?.configId ?? config.configId],
            fieldName: [config.fieldName],
            alternateFieldName: [existingValue?.alternateFieldName ?? config.alternateFieldName ?? null],
            dataType: [existingValue?.dataType ?? config.dataType],
            value: [existingValue?.value ?? null, validators],
            owningCompanyId: [existingValue?.owningCompanyId ?? this.owningCompanyId ?? config.owningCompanyId ?? null]
        });
    }

    private getConfig(index: number): FlexFieldConfig | undefined {
        return this.configs[index];
    }

    private getValueControl(index: number): AbstractControl | null {
        const group = this.controls[index];
        return group?.get('value') ?? null;
    }

    private getDataType(index: number): string {
        const group = this.controls[index];
        const groupType = (group?.get('dataType')?.value as string | undefined) ?? '';
        const configType = this.getConfig(index)?.dataType ?? groupType;
        return (configType ?? '').toString().toUpperCase();
    }

    private clearFormArray(): void {
        while (this.flexFields.length > 0) {
            this.flexFields.removeAt(0);
        }
    }

    private findFlexFieldValue(config: FlexFieldConfig, values: FlexFieldValue[]): FlexFieldValue | undefined {
        if (!values?.length) {
            return undefined;
        }
        const byId = values.find(value => value.configId != null && value.configId === config.configId);
        if (byId) {
            return byId;
        }
        const fieldKey = this.normalizeKey(config.fieldName);
        const alternateKey = this.normalizeKey(config.alternateFieldName);
        return values.find(value => {
            const valueField = this.normalizeKey(value.fieldName);
            const valueAlternate = this.normalizeKey(value.alternateFieldName);
            return valueField === fieldKey || valueField === alternateKey || valueAlternate === fieldKey;
        });
    }

    private isExcluded(config: FlexFieldConfig): boolean {
        const excluded = (this.excludedFieldNames ?? []).map(name => this.normalizeKey(name));
        const fieldName = this.normalizeKey(config.fieldName);
        const alternateFieldName = this.normalizeKey(config.alternateFieldName);
        return excluded.some(name => name != null && (name === fieldName || name === alternateFieldName));
    }

    private normalizeKey(value?: string | null): string | null {
        if (!value) {
            return null;
        }
        const trimmed = value.replace(/[^a-zA-Z0-9]/g, '').trim();
        return trimmed.length ? trimmed.toLowerCase() : null;
    }

    private cancelSubscription(): void {
        if (this.configSubscription) {
            this.configSubscription.unsubscribe();
            this.configSubscription = undefined;
        }
    }
}
